/**
 * AdminPanel — Backend control panel for the Connekted.in platform
 *
 * Sections:
 * - Dashboard (overview stats)
 * - User Management (list, search, ban/delete)
 * - Scan Analytics (platform-wide scan metrics)
 * - Referral Tracking (top referrers, total shares)
 * - Live Counter Control (set/override the homepage counter)
 * - Content Moderation (flagged profiles)
 *
 * API_TODO:
 * - GET /api/admin/stats → platform overview
 * - GET /api/admin/users?page=&search= → paginated user list
 * - PUT /api/admin/users/:id/status { status: 'active'|'banned' }
 * - DELETE /api/admin/users/:id → permanently delete user
 * - GET /api/admin/scans/stats → scan analytics
 * - GET /api/admin/referrals/stats → referral leaderboard
 * - PUT /api/admin/settings/counter { count: number }
 * - GET /api/admin/moderation/flagged → flagged profiles
 * - PUT /api/admin/moderation/:id { action: 'approve'|'ban' }
 *
 * Auth: Requires admin role. API_TODO: Add admin auth middleware.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import { useHelp } from '../context/HelpContext';
import { apiRequest } from '../lib/api';
import { adminLogout } from './AdminLogin';

// ── Mock Data ──
const EMPTY_STATS = {
  totalUsers: 0,
  activeToday: 0,
  totalScans: 0,
  scansToday: 0,
  totalReferrals: 0,
  conversionRate: 0,
};

const MOCK_REFERRAL_LEADERS = [];
const MOCK_FLAGGED = [];
const MOCK_FEEDBACK_REVIEWS = [];

function formatAdminDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const {
    helpTickets, featureRequests,
    openTickets, inProgressTickets, resolvedTickets,
    openFeatureRequests, inProgressFeatureRequests, dismissedFeatureRequests,
    startTicket, resolveTicket, reopenTicket,
    saveAdminResponse: saveAdminResponseCtx,
    startFeatureRequest, dismissFeatureRequest, reopenFeatureRequest,
    saveFeatureRequestResponse: saveFeatureRequestResponseCtx,
  } = useHelp();
  const [section, setSection] = useState('dashboard');
  const [stats, setStats] = useState(EMPTY_STATS);
  const [users, setUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(true);
  const [adminError, setAdminError] = useState('');
  const [flaggedProfiles, setFlaggedProfiles] = useState(MOCK_FLAGGED);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [frReplyDrafts, setFrReplyDrafts] = useState({});
  const [frReplyingTo, setFrReplyingTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [counterValue, setCounterValue] = useState(EMPTY_STATS.totalUsers);
  const [counterSaved, setCounterSaved] = useState(false);

  const loadAdminData = async () => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const [statsData, usersData] = await Promise.all([
        apiRequest('/admin/stats'),
        apiRequest('/admin/users')
      ]);
      setStats({ ...EMPTY_STATS, ...statsData });
      setUsers(usersData.users || []);
      setCounterValue(statsData.totalUsers || 0);
    } catch (error) {
      setAdminError(error.message || 'Unable to load admin data.');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  // All banned users: from users list + from flagged bans
  const bannedUsers = users.filter(u => u.status === 'banned');

  const adminSections = [
    { key: 'dashboard', label: 'Dashboard', icon: Icons.home },
    { key: 'users', label: 'Users', icon: Icons.user },
    { key: 'scans', label: 'Scan Analytics', icon: Icons.scan },
    { key: 'referrals', label: 'Referrals', icon: Icons.external },
    { key: 'counter', label: 'Live Counter', icon: Icons.trendDown },
    { key: 'moderation', label: 'Moderation', icon: Icons.shield },
    { key: 'helpSubmissions', label: 'Help Submissions', icon: Icons.help },
    { key: 'featureRequests', label: 'Feature Requests', icon: Icons.plus },
    { key: 'feedback', label: 'User Feedback', icon: Icons.star },
  ];

  const filteredUsers = searchQuery
    ? users.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.company || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const toggleUserStatus = async (id) => {
    const user = users.find((item) => item.id === id);
    if (!user) return;
    const nextStatus = user.status === 'active' ? 'banned' : 'active';

    setUsers(prev => prev.map(u =>
      u.id === id ? { ...u, status: nextStatus, bannedReason: nextStatus === 'banned' ? 'Banned from User Management' : undefined } : u
    ));

    try {
      await apiRequest(`/admin/users/${id}/status`, {
        method: 'PUT',
        body: { status: nextStatus }
      });
    } catch (error) {
      setUsers(prev => prev.map(u => (u.id === id ? user : u)));
      setAdminError(error.message || 'Unable to update user status.');
    }
  };

  // Approve a flagged profile → remove from flagged, add to users as active
  const approveFlagged = (flagged) => {
    // API_TODO: PUT /api/admin/moderation/:id { action: 'approve' }
    setFlaggedProfiles(prev => prev.filter(f => f.id !== flagged.id));
    setUsers(prev => [...prev, {
      id: flagged.userId,
      name: flagged.name,
      email: flagged.email,
      company: flagged.company,
      status: 'active',
      scans: flagged.scans || 0,
      joined: flagged.joined || new Date().toISOString().split('T')[0],
    }]);
  };

  // Ban a flagged profile → remove from flagged pending, add to users as banned
  const banFlagged = (flagged) => {
    // API_TODO: PUT /api/admin/moderation/:id { action: 'ban' }
    setFlaggedProfiles(prev => prev.filter(f => f.id !== flagged.id));
    setUsers(prev => [...prev, {
      id: flagged.userId,
      name: flagged.name,
      email: flagged.email,
      company: flagged.company,
      status: 'banned',
      scans: flagged.scans || 0,
      joined: flagged.joined || new Date().toISOString().split('T')[0],
      bannedReason: flagged.reason,
    }]);
  };

  const handleCounterSave = async () => {
    try {
      await apiRequest('/admin/settings/counter', {
        method: 'PUT',
        body: { count: counterValue }
      });
      setCounterSaved(true);
      setTimeout(() => setCounterSaved(false), 2000);
    } catch (error) {
      setAdminError(error.message || 'Unable to update counter.');
    }
  };

  // Save admin response — wraps context call with local draft cleanup
  const saveAdminResponse = (id) => {
    const text = (replyDrafts[id] || '').trim();
    if (!text) return;
    saveAdminResponseCtx(id, text);
    setReplyDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    setReplyingTo(null);
  };

  // Save feature request admin response
  const saveFrResponse = (id) => {
    const text = (frReplyDrafts[id] || '').trim();
    if (!text) return;
    saveFeatureRequestResponseCtx(id, text);
    setFrReplyDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    setFrReplyingTo(null);
  };

  return (
    <div className="app-shell">
      {/* Admin Sidebar */}
      <aside className="sidebar" style={{ background: '#1a1a2e' }}>
        <div className="sidebar-header" style={{ borderBottomColor: 'rgba(255,255,255,0.06)' }}>
          <AscendingK size={22} color="#C9A961" />
          <span className="sidebar-brand">
            Admin <em>Panel</em>
          </span>
        </div>

        <nav className="sidebar-nav">
          {adminSections.map(item => (
            <button
              key={item.key}
              className={`nav-item ${section === item.key ? 'active' : ''}`}
              onClick={() => setSection(item.key)}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="nav-item" onClick={() => { adminLogout(); navigate('/admin/login'); }}>
            {Icons.lock} Logout
          </button>
          <a href="/" className="nav-item" style={{ textDecoration: 'none' }}>
            {Icons.home} Back to site
          </a>
        </nav>

        <div className="sidebar-footer" style={{ color: 'rgba(255,255,255,0.2)' }}>
          Admin v1.0
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {adminError && (
          <div className="panel" style={{ marginBottom: 20, borderColor: 'rgba(220, 53, 69, 0.25)', color: '#DC3545' }}>
            {adminError}
          </div>
        )}

        {/* ── Dashboard ── */}
        {section === 'dashboard' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>Admin Dashboard</div>
              <h1 className="page-title">Platform <em>overview</em>.</h1>
            </div>
            <div className="page-body">
              <div className="admin-stats-grid">
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Total Users</div>
                  <div className="admin-stat-value serif">{stats.totalUsers.toLocaleString()}</div>
                  <div className="admin-stat-sub">+{stats.activeToday} active today</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Total Scans</div>
                  <div className="admin-stat-value serif">{stats.totalScans.toLocaleString()}</div>
                  <div className="admin-stat-sub">+{stats.scansToday} today</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Referrals</div>
                  <div className="admin-stat-value serif">{stats.totalReferrals.toLocaleString()}</div>
                  <div className="admin-stat-sub">{stats.conversionRate}% conversion</div>
                </div>
                <div className="admin-stat-card accent">
                  <div className="admin-stat-label mono" style={{ color: 'rgba(245,242,235,0.5)' }}>Growth Rate</div>
                  <div className="admin-stat-value serif" style={{ color: 'var(--gold)' }}>+18.4%</div>
                  <div className="admin-stat-sub" style={{ color: 'rgba(245,242,235,0.4)' }}>Month over month</div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Quick Actions</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setSection('users')}>
                    {Icons.user} Manage Users
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => setSection('moderation')}>
                    {Icons.shield} Review Flagged
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={() => setSection('counter')}>
                    {Icons.edit} Update Counter
                  </button>
                </div>
              </div>

              {/* Recent signups */}
              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Recent Sign-ups</div>
                {adminLoading && (
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading registered users...</div>
                )}
                {!adminLoading && users.slice(0, 5).map(u => (
                  <div key={u.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: '1px solid var(--rule)'
                  }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.company || 'No company'} &middot; {u.email}</div>
                    </div>
                    <div className="mono" style={{ fontSize: 9 }}>{formatAdminDate(u.joined)}</div>
                  </div>
                ))}
                {!adminLoading && users.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--muted)' }}>No registered users yet.</div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── User Management ── */}
        {section === 'users' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>User Management</div>
              <h1 className="page-title">All <em>users</em>.</h1>
              <p className="page-subtitle">{users.length} registered users</p>
            </div>
            <div className="page-body">
              <div style={{ marginBottom: 20 }}>
                <input
                  className="form-input"
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ maxWidth: 400 }}
                />
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Company</th>
                      <th>Scans</th>
                      <th>Joined</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminLoading && (
                      <tr>
                        <td colSpan="6" style={{ color: 'var(--muted)', padding: 20 }}>Loading registered users...</td>
                      </tr>
                    )}
                    {!adminLoading && filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ color: 'var(--muted)', padding: 20 }}>No users found.</td>
                      </tr>
                    )}
                    {filteredUsers.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{u.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.email}</div>
                        </td>
                        <td>{u.company || '-'}</td>
                        <td>{u.scans}</td>
                        <td className="mono" style={{ fontSize: 10 }}>{formatAdminDate(u.joined)}</td>
                        <td>
                          <span className={`admin-status ${u.status}`}>
                            {u.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className={`btn btn-sm ${u.status === 'active' ? 'btn-outline' : 'btn-primary-app'}`}
                            onClick={() => toggleUserStatus(u.id)}
                            style={{ fontSize: 11 }}
                          >
                            {u.status === 'active' ? 'Ban' : 'Unban'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Scan Analytics ── */}
        {section === 'scans' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>Scan Analytics</div>
              <h1 className="page-title">Platform <em>scans</em>.</h1>
            </div>
            <div className="page-body">
              <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Total Scans</div>
                  <div className="admin-stat-value serif">{stats.totalScans.toLocaleString()}</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Today</div>
                  <div className="admin-stat-value serif">{stats.scansToday.toLocaleString()}</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Avg / User</div>
                  <div className="admin-stat-value serif">{stats.totalUsers ? (stats.totalScans / stats.totalUsers).toFixed(1) : '0.0'}</div>
                </div>
              </div>

              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Top Cards by Scans</div>
                {[...users].sort((a, b) => b.scans - a.scans).slice(0, 5).map((u, i) => (
                  <div key={u.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 0', borderBottom: '1px solid var(--rule)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span className="mono" style={{ width: 20, color: 'var(--muted)' }}>#{i + 1}</span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{u.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.company || 'No company'}</div>
                      </div>
                    </div>
                    <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, color: 'var(--brand)' }}>
                      {u.scans}
                    </div>
                  </div>
                ))}
              </div>

              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Scan Activity (last 7 days)</div>
                {/* API_TODO: Replace with actual chart — use recharts or Chart.js */}
                <div style={{
                  height: 160, display: 'flex', alignItems: 'flex-end', gap: 8,
                  padding: '20px 0'
                }}>
                  {[62, 78, 45, 91, 120, 147, 103].map((v, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{
                        width: '100%', height: `${(v / 150) * 120}px`,
                        background: i === 5 ? 'var(--brand)' : 'var(--paper-deep)',
                        borderRadius: '4px 4px 0 0', transition: 'height 0.3s'
                      }} />
                      <span className="mono" style={{ fontSize: 8 }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Referral Tracking ── */}
        {section === 'referrals' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>Referral Tracking</div>
              <h1 className="page-title">Referral <em>leaders</em>.</h1>
            </div>
            <div className="page-body">
              <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Total Shares</div>
                  <div className="admin-stat-value serif">892</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Sign-ups via Referral</div>
                  <div className="admin-stat-value serif">217</div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-label mono">Conversion Rate</div>
                  <div className="admin-stat-value serif">24.3%</div>
                </div>
              </div>

              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Leaderboard</div>
                {MOCK_REFERRAL_LEADERS.map(r => (
                  <div key={r.rank} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0', borderBottom: '1px solid var(--rule)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{
                        fontFamily: "'Fraunces', serif", fontSize: 20, width: 32,
                        color: r.rank <= 3 ? 'var(--gold)' : 'var(--muted)'
                      }}>
                        {r.rank}
                      </span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 15 }}>{r.name}</div>
                        <div className="mono" style={{ fontSize: 9 }}>
                          {r.shares} shares &middot; {r.signups} sign-ups
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 14,
                      color: 'var(--success)', fontWeight: 600
                    }}>
                      {r.points} pts
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Live Counter Control ── */}
        {section === 'counter' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>Live Counter</div>
              <h1 className="page-title">Homepage <em>counter</em>.</h1>
              <p className="page-subtitle">Control the "Trusted by X professionals" counter on the landing page.</p>
            </div>
            <div className="page-body">
              <div className="panel">
                <div className="panel-label">Current Counter Value</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                  <input
                    className="form-input"
                    type="number"
                    value={counterValue}
                    onChange={e => { setCounterValue(parseInt(e.target.value) || 0); setCounterSaved(false); }}
                    style={{ maxWidth: 200, fontSize: 24, fontFamily: "'Fraunces', serif", textAlign: 'center' }}
                  />
                  <button className="btn btn-primary-app" onClick={handleCounterSave}>
                    {counterSaved ? <>{Icons.check} Saved</> : 'Update Counter'}
                  </button>
                </div>
                <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                  This value appears on the homepage as "Trusted by X professionals and counting."
                  In production, this should auto-increment when new users sign up.
                  Use this control to manually override the displayed number if needed.
                </p>
              </div>

              <div className="panel" style={{ marginTop: 24 }}>
                <div className="panel-label">Counter Settings</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                    <input type="checkbox" defaultChecked style={{ width: 18, height: 18 }} />
                    Auto-increment on new sign-up
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                    <input type="checkbox" defaultChecked style={{ width: 18, height: 18 }} />
                    Show animated count-up on scroll
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                    <input type="checkbox" style={{ width: 18, height: 18 }} />
                    Show "Live" pulse indicator
                  </label>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Content Moderation ── */}
        {section === 'moderation' && (
          <>
            <div className="page-header">
              <div className="mono" style={{ marginBottom: 8 }}>Content Moderation</div>
              <h1 className="page-title">Flagged <em>profiles</em>.</h1>
              <p className="page-subtitle">
                {flaggedProfiles.length} pending review &middot; {bannedUsers.length} banned
              </p>
            </div>
            <div className="page-body">
              {/* Pending flagged profiles */}
              <div className="panel" style={{ marginBottom: 24 }}>
                <div className="panel-label">Pending Review</div>
                {flaggedProfiles.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                    <p className="serif" style={{ fontSize: 18, fontStyle: 'italic', margin: 0 }}>All clear.</p>
                    <p style={{ fontSize: 13, marginTop: 6 }}>No flagged profiles to review.</p>
                  </div>
                ) : (
                  flaggedProfiles.map(item => (
                    <div key={item.id} style={{
                      padding: '16px 0',
                      borderBottom: '1px solid var(--rule)',
                      borderLeft: '3px solid var(--danger)',
                      paddingLeft: 16,
                      marginBottom: 4,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 500 }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                            {item.email} &middot; {item.company}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {Icons.warning} {item.reason}
                          </div>
                          <div className="mono" style={{ marginTop: 8 }}>Reported {item.reportedAt}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ color: 'var(--success)', borderColor: 'var(--success)' }}
                            onClick={() => approveFlagged(item)}
                          >
                            {Icons.check} Approve
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                            onClick={() => banFlagged(item)}
                          >
                            Ban User
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Banned users section — from both flagged bans and user management bans */}
              <div className="panel">
                <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  Banned Users
                  {bannedUsers.length > 0 && (
                    <span style={{
                      background: 'var(--danger)', color: 'white', fontSize: 11,
                      padding: '1px 8px', borderRadius: 10, fontWeight: 600
                    }}>
                      {bannedUsers.length}
                    </span>
                  )}
                </div>
                {bannedUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                    <p style={{ fontSize: 14, margin: 0 }}>No banned users.</p>
                  </div>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Company</th>
                          <th>Reason</th>
                          <th>Joined</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bannedUsers.map(u => (
                          <tr key={u.id}>
                            <td>
                              <div style={{ fontWeight: 500 }}>{u.name}</div>
                              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{u.email}</div>
                            </td>
                            <td>{u.company}</td>
                            <td style={{ fontSize: 12, color: 'var(--danger)' }}>
                              {u.bannedReason || 'Banned from User Management'}
                            </td>
                            <td className="mono" style={{ fontSize: 10 }}>{u.joined}</td>
                            <td>
                              <button
                                className="btn btn-primary-app btn-sm"
                                style={{ fontSize: 11 }}
                                onClick={() => toggleUserStatus(u.id)}
                              >
                                Unban
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Help Submissions ── */}
        {section === 'helpSubmissions' && (() => {
          // Shared ticket card renderer with admin response
          const renderTicketCard = (item, borderColor, statusBadge, actions, dimmed) => (
            <div key={item.id} style={{
              padding: '16px 0', borderBottom: '1px solid var(--rule)',
              borderLeft: `3px solid ${borderColor}`, paddingLeft: 16, marginBottom: 4,
              opacity: dimmed ? 0.75 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{item.user}</span>
                    {statusBadge}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--brand)', marginBottom: 6, fontWeight: 500 }}>
                    {item.typeLabel}
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.6, margin: 0 }}>
                    {item.description}
                  </p>
                  <div className="mono" style={{ marginTop: 8, fontSize: 9 }}>
                    Submitted {new Date(item.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {item.startedAt && <> &middot; Started {new Date(item.startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                    {item.resolvedAt && <> &middot; Resolved {new Date(item.resolvedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                  {actions}
                </div>
              </div>

              {/* Admin response display */}
              {item.adminResponse && (
                <div className="admin-response-box">
                  <div className="admin-response-label">Admin Response</div>
                  <p className="admin-response-text">{item.adminResponse}</p>
                  {item.respondedAt && (
                    <div className="mono" style={{ fontSize: 9, marginTop: 4 }}>
                      {new Date(item.respondedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              )}

              {/* Reply / Edit response toggle */}
              {replyingTo === item.id ? (
                <div className="admin-reply-box">
                  <textarea
                    className="admin-reply-input"
                    placeholder="Write a response visible to the user..."
                    value={replyDrafts[item.id] || item.adminResponse || ''}
                    onChange={e => setReplyDrafts(prev => ({ ...prev, [item.id]: e.target.value }))}
                    rows={3}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button className="btn btn-ghost-app btn-sm" style={{ fontSize: 11 }}
                      onClick={() => { setReplyingTo(null); setReplyDrafts(prev => { const n = { ...prev }; delete n[item.id]; return n; }); }}>
                      Cancel
                    </button>
                    <button className="btn btn-primary-app btn-sm" style={{ fontSize: 11 }}
                      onClick={() => saveAdminResponse(item.id)}
                      disabled={!(replyDrafts[item.id] || '').trim()}>
                      {item.adminResponse ? 'Update Response' : 'Send Response'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="admin-reply-toggle"
                  onClick={() => { setReplyingTo(item.id); if (item.adminResponse) setReplyDrafts(prev => ({ ...prev, [item.id]: item.adminResponse })); }}
                >
                  {Icons.edit} {item.adminResponse ? 'Edit response' : 'Add response'}
                </button>
              )}
            </div>
          );

          return (
            <>
              <div className="page-header">
                <div className="mono" style={{ marginBottom: 8 }}>Help Submissions</div>
                <h1 className="page-title">User <em>issues</em>.</h1>
                <p className="page-subtitle">
                  {openTickets.length} open &middot; {inProgressTickets.length} in progress &middot; {resolvedTickets.length} resolved
                </p>
              </div>
              <div className="page-body">
                {/* ── Open tickets ── */}
                <div className="panel" style={{ marginBottom: 24 }}>
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Open
                    {openTickets.length > 0 && (
                      <span style={{ background: 'var(--danger)', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {openTickets.length}
                      </span>
                    )}
                  </div>
                  {openTickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p className="serif" style={{ fontSize: 18, fontStyle: 'italic', margin: 0 }}>No open issues.</p>
                      <p style={{ fontSize: 13, marginTop: 6 }}>All issues have been picked up or resolved.</p>
                    </div>
                  ) : (
                    openTickets.map(item => renderTicketCard(item, 'var(--danger)',
                      <span className="admin-status banned">open</span>,
                      <>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: '#856404', borderColor: '#856404' }} onClick={() => startTicket(item.id)}>Start Progress</button>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--success)', borderColor: 'var(--success)' }} onClick={() => resolveTicket(item.id)}>{Icons.check} Resolve</button>
                      </>
                    ))
                  )}
                </div>

                {/* ── In Progress tickets ── */}
                <div className="panel" style={{ marginBottom: 24 }}>
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    In Progress
                    {inProgressTickets.length > 0 && (
                      <span style={{ background: '#856404', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {inProgressTickets.length}
                      </span>
                    )}
                  </div>
                  {inProgressTickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p style={{ fontSize: 14, margin: 0 }}>No issues in progress.</p>
                    </div>
                  ) : (
                    inProgressTickets.map(item => renderTicketCard(item, '#DAA520',
                      <span className="admin-status" style={{ background: '#FFF3CD', color: '#856404' }}>in progress</span>,
                      <>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => reopenTicket(item.id)}>Back to Open</button>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--success)', borderColor: 'var(--success)' }} onClick={() => resolveTicket(item.id)}>{Icons.check} Resolve</button>
                      </>
                    ))
                  )}
                </div>

                {/* ── Resolved tickets ── */}
                <div className="panel">
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Solved
                    {resolvedTickets.length > 0 && (
                      <span style={{ background: 'var(--success)', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {resolvedTickets.length}
                      </span>
                    )}
                  </div>
                  {resolvedTickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p style={{ fontSize: 14, margin: 0 }}>No resolved issues yet.</p>
                    </div>
                  ) : (
                    resolvedTickets.map(item => renderTicketCard(item, 'var(--success)',
                      <span className="admin-status active">resolved</span>,
                      <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => reopenTicket(item.id)}>Reopen</button>,
                      true
                    ))
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* ── Feature Requests ── */}
        {section === 'featureRequests' && (() => {
          // Shared feature request card renderer with admin response
          const renderFrCard = (item, borderColor, statusBadge, actions, dimmed) => (
            <div key={item.id} style={{
              padding: '16px 0', borderBottom: '1px solid var(--rule)',
              borderLeft: `3px solid ${borderColor}`, paddingLeft: 16, marginBottom: 4,
              opacity: dimmed ? 0.75 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{item.user}</span>
                    {statusBadge}
                  </div>
                  <p style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.6, margin: 0 }}>
                    {item.description}
                  </p>
                  <div className="mono" style={{ marginTop: 8, fontSize: 9 }}>
                    Submitted {new Date(item.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {item.startedAt && <> &middot; Started {new Date(item.startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                    {item.dismissedAt && <> &middot; Dismissed {new Date(item.dismissedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginLeft: 12, flexShrink: 0 }}>
                  {actions}
                </div>
              </div>

              {/* Admin response display */}
              {item.adminResponse && (
                <div className="admin-response-box">
                  <div className="admin-response-label">Admin Response</div>
                  <p className="admin-response-text">{item.adminResponse}</p>
                  {item.respondedAt && (
                    <div className="mono" style={{ fontSize: 9, marginTop: 4 }}>
                      {new Date(item.respondedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
              )}

              {/* Reply / Edit response toggle */}
              {frReplyingTo === item.id ? (
                <div className="admin-reply-box">
                  <textarea
                    className="admin-reply-input"
                    placeholder="Write a response visible to the user..."
                    value={frReplyDrafts[item.id] || item.adminResponse || ''}
                    onChange={e => setFrReplyDrafts(prev => ({ ...prev, [item.id]: e.target.value }))}
                    rows={3}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                    <button className="btn btn-ghost-app btn-sm" style={{ fontSize: 11 }}
                      onClick={() => { setFrReplyingTo(null); setFrReplyDrafts(prev => { const n = { ...prev }; delete n[item.id]; return n; }); }}>
                      Cancel
                    </button>
                    <button className="btn btn-primary-app btn-sm" style={{ fontSize: 11 }}
                      onClick={() => saveFrResponse(item.id)}
                      disabled={!(frReplyDrafts[item.id] || '').trim()}>
                      {item.adminResponse ? 'Update Response' : 'Send Response'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="admin-reply-toggle"
                  onClick={() => { setFrReplyingTo(item.id); if (item.adminResponse) setFrReplyDrafts(prev => ({ ...prev, [item.id]: item.adminResponse })); }}
                >
                  {Icons.edit} {item.adminResponse ? 'Edit response' : 'Add response'}
                </button>
              )}
            </div>
          );

          return (
            <>
              <div className="page-header">
                <div className="mono" style={{ marginBottom: 8 }}>Feature Requests</div>
                <h1 className="page-title">User <em>ideas</em>.</h1>
                <p className="page-subtitle">
                  {openFeatureRequests.length} open &middot; {inProgressFeatureRequests.length} in progress &middot; {dismissedFeatureRequests.length} dismissed
                </p>
              </div>
              <div className="page-body">
                {/* ── Open feature requests ── */}
                <div className="panel" style={{ marginBottom: 24 }}>
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Open
                    {openFeatureRequests.length > 0 && (
                      <span style={{ background: 'var(--brand)', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {openFeatureRequests.length}
                      </span>
                    )}
                  </div>
                  {openFeatureRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p className="serif" style={{ fontSize: 18, fontStyle: 'italic', margin: 0 }}>No open requests.</p>
                      <p style={{ fontSize: 13, marginTop: 6 }}>All feature requests have been reviewed.</p>
                    </div>
                  ) : (
                    openFeatureRequests.map(item => renderFrCard(item, 'var(--brand)',
                      <span className="admin-status" style={{ background: '#E8F0FE', color: '#1A56DB' }}>open</span>,
                      <>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: '#856404', borderColor: '#856404' }} onClick={() => startFeatureRequest(item.id)}>In Progress</button>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--muted)', borderColor: 'var(--muted)' }} onClick={() => dismissFeatureRequest(item.id)}>Dismiss</button>
                      </>
                    ))
                  )}
                </div>

                {/* ── In Progress feature requests ── */}
                <div className="panel" style={{ marginBottom: 24 }}>
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    In Progress
                    {inProgressFeatureRequests.length > 0 && (
                      <span style={{ background: '#856404', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {inProgressFeatureRequests.length}
                      </span>
                    )}
                  </div>
                  {inProgressFeatureRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p style={{ fontSize: 14, margin: 0 }}>No feature requests in progress.</p>
                    </div>
                  ) : (
                    inProgressFeatureRequests.map(item => renderFrCard(item, '#DAA520',
                      <span className="admin-status" style={{ background: '#FFF3CD', color: '#856404' }}>in progress</span>,
                      <>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => reopenFeatureRequest(item.id)}>Back to Open</button>
                        <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--muted)', borderColor: 'var(--muted)' }} onClick={() => dismissFeatureRequest(item.id)}>Dismiss</button>
                      </>
                    ))
                  )}
                </div>

                {/* ── Dismissed feature requests ── */}
                <div className="panel">
                  <div className="panel-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    Dismissed
                    {dismissedFeatureRequests.length > 0 && (
                      <span style={{ background: 'var(--muted)', color: 'white', fontSize: 11, padding: '1px 8px', borderRadius: 10, fontWeight: 600 }}>
                        {dismissedFeatureRequests.length}
                      </span>
                    )}
                  </div>
                  {dismissedFeatureRequests.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>
                      <p style={{ fontSize: 14, margin: 0 }}>No dismissed requests.</p>
                    </div>
                  ) : (
                    dismissedFeatureRequests.map(item => renderFrCard(item, 'var(--muted)',
                      <span className="admin-status">dismissed</span>,
                      <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => reopenFeatureRequest(item.id)}>Reopen</button>,
                      true
                    ))
                  )}
                </div>
              </div>
            </>
          );
        })()}

        {/* ── User Feedback ── */}
        {section === 'feedback' && (() => {
          const reviews = MOCK_FEEDBACK_REVIEWS;
          const avgOverall = reviews.length > 0
            ? (reviews.reduce((sum, r) => sum + r.averageRating, 0) / reviews.length).toFixed(1)
            : '—';
          const categoryLabels = {
            ease_of_use: 'Ease of Use',
            profile_editor: 'Profile Editor',
            qr_quality: 'QR Quality',
            scan_notifications: 'Notifications',
            overall: 'Overall',
          };
          // Calculate per-category averages
          const categoryAvgs = {};
          Object.keys(categoryLabels).forEach(key => {
            const vals = reviews.map(r => r.ratings[key]).filter(Boolean);
            categoryAvgs[key] = vals.length > 0
              ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)
              : '—';
          });

          const renderStars = (rating) => (
            <span style={{ display: 'inline-flex', gap: 2 }}>
              {[1, 2, 3, 4, 5].map(s => (
                <svg key={s} width="14" height="14" viewBox="0 0 24 24"
                  fill={s <= Math.round(rating) ? '#C9A961' : 'none'}
                  stroke={s <= Math.round(rating) ? '#C9A961' : '#ccc'}
                  strokeWidth="1.5"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </span>
          );

          return (
            <>
              <div className="page-header">
                <div className="mono" style={{ marginBottom: 8 }}>User Feedback</div>
                <h1 className="page-title">Platform <em>reviews</em>.</h1>
                <p className="page-subtitle">{reviews.length} feedback submissions</p>
              </div>
              <div className="page-body">
                {/* Summary stats */}
                <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
                  <div className="admin-stat-card accent">
                    <div className="admin-stat-label mono" style={{ color: 'rgba(245,242,235,0.5)' }}>Avg Rating</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="admin-stat-value serif" style={{ color: 'var(--gold)' }}>{avgOverall}</div>
                      {renderStars(parseFloat(avgOverall))}
                    </div>
                    <div className="admin-stat-sub" style={{ color: 'rgba(245,242,235,0.4)' }}>{reviews.length} reviews</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-label mono">Highest Rated</div>
                    <div className="admin-stat-value serif" style={{ fontSize: 20 }}>
                      {Object.entries(categoryAvgs).sort((a, b) => b[1] - a[1])[0]?.[0]
                        ? categoryLabels[Object.entries(categoryAvgs).sort((a, b) => b[1] - a[1])[0][0]]
                        : '—'}
                    </div>
                    <div className="admin-stat-sub">
                      {Object.entries(categoryAvgs).sort((a, b) => b[1] - a[1])[0]?.[1] || '—'} avg
                    </div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-label mono">Lowest Rated</div>
                    <div className="admin-stat-value serif" style={{ fontSize: 20 }}>
                      {Object.entries(categoryAvgs).sort((a, b) => a[1] - b[1])[0]?.[0]
                        ? categoryLabels[Object.entries(categoryAvgs).sort((a, b) => a[1] - b[1])[0][0]]
                        : '—'}
                    </div>
                    <div className="admin-stat-sub">
                      {Object.entries(categoryAvgs).sort((a, b) => a[1] - b[1])[0]?.[1] || '—'} avg
                    </div>
                  </div>
                </div>

                {/* Per-category breakdown */}
                <div className="panel" style={{ marginBottom: 24 }}>
                  <div className="panel-label">Rating Breakdown</div>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <div key={key} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '12px 0', borderBottom: '1px solid var(--rule)'
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {renderStars(parseFloat(categoryAvgs[key]))}
                        <span className="mono" style={{ fontSize: 13, width: 28, textAlign: 'right' }}>
                          {categoryAvgs[key]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Individual reviews */}
                <div className="panel">
                  <div className="panel-label">All Reviews</div>
                  {reviews.map(review => (
                    <div key={review.id} style={{
                      padding: '16px 0', borderBottom: '1px solid var(--rule)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{review.user}</span>
                          {renderStars(review.averageRating)}
                          <span className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                            {review.averageRating.toFixed(1)}
                          </span>
                        </div>
                        <span className="mono" style={{ fontSize: 9 }}>
                          {new Date(review.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>

                      {/* Individual category ratings */}
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: review.comment ? 10 : 0 }}>
                        {Object.entries(review.ratings).map(([key, val]) => (
                          <span key={key} style={{ fontSize: 12, color: 'var(--muted)' }}>
                            {categoryLabels[key]}: <strong style={{ color: 'var(--ink)' }}>{val}</strong>
                          </span>
                        ))}
                      </div>

                      {review.comment && (
                        <p style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                          "{review.comment}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}
      </main>
    </div>
  );
}
